import bindings from "./published-landing-bindings.json" with { type: "json" };
import { LEGACY_LP_PRIMARIES, legacyLpDisposition } from "./legacy-lp-readiness.mjs";
import { sitemapCanonicalLandingPath, SITEMAP_LANDING_SOURCE_REDIRECTS } from "./sitemap-canonical-projection.mjs";

// A small projection of the very same authored owners, not a second authority.
// Shared browser chunks never need all the editorial bodies.
const records = new Map(bindings.records.map(record => [record.path, record]));
const regionalSource = "/be/location-voiture-classique-sans-chauffeur-liege";
const regionalPrimary = SITEMAP_LANDING_SOURCE_REDIRECTS[regionalSource];
function key(pathname) {
  try { return "/" + decodeURI(pathname).replace(/^\/+|\/+$/g, ""); }
  catch { return null; }
}
function direct(pathname) {
  const path = publishedLandingLookupPath(pathname), record = records.get(path);
  return path && (!record || record.state === "direct") ? bindings.pages[path] : undefined;
}
// The French Belgian body already declares fr-BE. Its approved qualified
// primary must resolve that SAME owner, not require a second CMS publication.
export function publishedLandingLookupPath(pathname) {
  const path = key(pathname);
  return path === regionalPrimary && !bindings.pages[path] ? regionalSource : path;
}
function publishedAuthority(context) {
  try {
    return ["www.bookaclassic.com", "bookaclassic.com"].includes(new URL(context?.origin).hostname);
  } catch { return false; }
}

// Classification is not publication: an absent/retired authored record still
// must not be consumed by the legacy-car Gone handler.
export function hasRegisteredAuthoredLandingSource(pathname, context) {
  if (!publishedAuthority(context)) return false;
  const sourcePath = publishedLandingLookupPath(pathname);
  return records.has(sourcePath) || Object.hasOwn(bindings.pages, sourcePath);
}

export function publishedLandingSourcePaths(context) {
  return publishedAuthority(context) ? [
    ...new Set(Object.keys(bindings.pages).filter(path => direct(path) && !legacyLpDisposition(path))
      .map(sitemapCanonicalLandingPath)),
    ...LEGACY_LP_PRIMARIES.filter(path => direct(legacyLpDisposition(path).contentPath)),
  ] : [];
}
export function compiledPublishedLandingSource(pathname, context, loadViewModel) {
  if (!publishedAuthority(context)) return undefined;
  const scoped = legacyLpDisposition(pathname);
  const sourcePath = scoped?.kind === "page" ? scoped.contentPath : pathname;
  const record = records.get(publishedLandingLookupPath(sourcePath));
  // Exact redirects in the authored registry are published source records too.
  // Their existing terminal owner supplies the body identity and canonical;
  // enumeration still includes canonical pages only, never aliases or proposals.
  const page = direct(record?.state === "redirect" ? record.target : sourcePath);
  if (!page) return undefined;
  const canonicalPath = sitemapCanonicalLandingPath("/" + page.full_path);
  return {
    sourceReference: `published-authored:${bindings.sourceManifestSha256}:${page.id}`,
    preferCms: false,
    row: { kind: "landing", contentIdentity: page.id, canonicalPath,
      publication: page.active ? "published" : "unpublished",
      indexable: page.is_indexable, language: page.language, languageVerified: true },
    ...(loadViewModel ? { viewModel: () => loadViewModel(canonicalPath) } : {}),
  };
}

/** The immutable published owner, not the URL prefix, establishes stock country.
 * Ordinary wrapper rules and complete stock-DTO identity checks stay intact.
 */
export function publishedLandingStockIdentity(data, pathname, brand) {
  if (brand !== "bookaclassic" || !data?.page ||
      data.page.sourceProvenance?.kind !== "published-authored-source") return null;
  const expected = direct(pathname), page = data.page;
  if (!expected) throw Error("Unknown published source stock owner");
  if (page.id !== expected.id || page.full_path !== expected.full_path ||
      page.language !== expected.language || data.matchedPath !== expected.full_path ||
      page.active !== expected.active || page.is_indexable !== expected.is_indexable ||
      page.sourceProvenance.manifestSha256 !== bindings.sourceManifestSha256 ||
      page.filter_params?.country_code !== expected.countryCode)
    throw Error("Published source stock DTO binding changed");
  const identity = { path: "/" + expected.full_path,
    countryCode: expected.countryCode,
    locale: expected.language.toLowerCase().split("-")[0], brand };
  const stock = data.fixedInventory;
  if (stock?.state === "ready" && (stock.path !== identity.path ||
      stock.countryCode !== identity.countryCode || stock.locale !== identity.locale ||
      stock.brand !== identity.brand))
    throw Object.assign(Error("Published source stock DTO identity mismatch"), { status: 503 });
  return identity;
}
